import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { BaseVariantTabProps } from '@storefronts/common-types'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    ACCENT_FILL_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_DISCOUNT_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { scaledFontSize } from '../../style/scrollingLayout'
import { getTypographyRoleStyle } from '../../style/typography'

// Theme Configuration
export const VARIANT_THEME = {
    layout: {
        spacing: {
            xxs: '4px',
            xs: '6px',
            sm: '8px',
            md: '12px',
            lg: '16px',
            xl: '24px',
        },
        sizes: {
            circular: {
                default: '36px',
                mobile: '32px',
            },
            image: {
                default: '64px',
                mobile: '56px',
                thumbnail: '70px',
            },
            swatch: {
                default: '30px',
                container: '38px',
            },
        },
        radius: {
            circular: '50%',
            standard: '4px',
            image: '6px',
            full: '18px',
        },
    },
    colors: {
        text: {
            primary: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
            secondary: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
            inverse: `var(${ACCENT_TEXT_COLOR_VAR})`,
            muted: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
        },
        states: {
            hover: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 10%, transparent 90%)`,
            active: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
            disabled: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 10%, transparent 90%)`,
            selected: {
                border: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                background: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 10%, transparent 90%)`,
            },
        },
        feedback: {
            error: `var(${SECONDARY_DISCOUNT_COLOR_VAR})`,
            warning: `var(${ACCENT_FILL_COLOR_VAR})`,
            success: `var(${ACCENT_FILL_COLOR_VAR})`,
        },
        borders: {
            light: `var(${SUBTLE_BORDER_COLOR_VAR})`,
            selected: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
            transparent: 'transparent',
        },
    },
    typography: {
        sizes: {
            xs: scaledFontSize(9),
            sm: scaledFontSize(10),
            base: scaledFontSize(11),
            md: scaledFontSize(12),
            lg: scaledFontSize(13),
        },
        weights: {
            regular: 400,
            medium: 500,
            bold: 700,
        },
        lineHeights: {
            tight: 1,
            base: 1.2,
            relaxed: 1.4,
        },
    },
    transitions: {
        smooth: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    shadows: {
        subtle: '0 0 0 1px rgba(0,0,0,0.04)',
        active: '0 0 0 1px rgba(0,0,0,0.04), 0 4px 10px rgba(0,0,0,0.06)',
    },
    breakpoints: {
        mobile: '768px',
        tablet: '1024px',
    },
    overlays: {
        soldOut: `color-mix(in srgb, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 70%, transparent 30%)`,
    },
    borders: {
        opacity: {
            unavailable: 8,
            available: 30,
            active: 100,
        },
    },
} as const

// Base Styles
const baseTabStyles = `
    position: relative;
    cursor: pointer;
    transition: ${VARIANT_THEME.transitions.smooth};
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 1px solid var(${SUBTLE_PRIMARY_COLOR_VAR});
        outline-offset: 1px;
    }

    &:active {
        transform: scale(0.98);
    }
`

// Utility Styles
const getUnavailableStyles = (available: boolean, isClickable?: boolean) => {
    const showUnavailableVisuals = !available
    const showNotAllowedCursor = !(isClickable ?? available)

    if (!showUnavailableVisuals && !showNotAllowedCursor) return ''

    return `
    ${showNotAllowedCursor ? 'cursor: not-allowed;' : ''}
    ${
        showUnavailableVisuals
            ? `
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${VARIANT_THEME.overlays.soldOut};
        border-radius: inherit;
    }

    &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: -2px;
        right: -2px;
        height: 1px;
        background: color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%);
        transform: rotate(-45deg);
        pointer-events: none;
    }
`
            : ''
    }
`
}

// Component Styles
export const TabsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${VARIANT_THEME.layout.spacing.xs};
    padding: ${VARIANT_THEME.layout.spacing.xxs} 0;
    align-items: center;
`

const BaseImageTab = styled.div<BaseVariantTabProps>`
    ${baseTabStyles}
    width: ${VARIANT_THEME.layout.sizes.image.default};
    height: ${VARIANT_THEME.layout.sizes.image.default};
    border-radius: ${VARIANT_THEME.layout.radius.image};
    /* Let the outer VariantImageContainer handle the visible border so we don't
       end up with a double outline. */
    border: none;
    box-shadow: ${(props) =>
        props.active ? VARIANT_THEME.shadows.subtle : 'none'};
    transition: ${VARIANT_THEME.transitions.smooth};

    ${(props) => getUnavailableStyles(props.available, props.isClickable)}

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        width: ${VARIANT_THEME.layout.sizes.image.mobile};
        height: ${VARIANT_THEME.layout.sizes.image.mobile};
    }
`

const swatchSizeStyles = `
    width: ${VARIANT_THEME.layout.sizes.swatch.default};
    height: ${VARIANT_THEME.layout.sizes.swatch.default};
    border-radius: 50%;
    border: 1px solid #ddd;

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        width: ${VARIANT_THEME.layout.sizes.swatch.default};
        height: ${VARIANT_THEME.layout.sizes.swatch.default};
    }
`

export const ImageTab = styled(BaseImageTab)<{
    imageUrl: string
    isSwatch?: boolean
}>`
    background: url(${(props) => props.imageUrl}) center/cover;

    ${(props) => props.isSwatch && swatchSizeStyles}
`

export const ColorSwatchTab = styled(BaseImageTab)<{
    colorValue: string
    isSwatch?: boolean
}>`
    background-color: ${(props) => props.colorValue};
    min-width: unset;
    min-height: unset;
    border: 1px solid #ddd;

    ${(props) => props.isSwatch && swatchSizeStyles}
`

export const Tab = styled.div<BaseVariantTabProps>`
    ${baseTabStyles}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    padding: ${VARIANT_THEME.layout.spacing.xs}
        ${VARIANT_THEME.layout.spacing.md};
    border-radius: 0.2rem;
    box-sizing: border-box;
    background: ${(props) =>
        props.active ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})` : 'transparent'};
    border-width: 1px;
    border-style: solid;
    border-color: ${(props) => {
        if (props.active) {
            return VARIANT_THEME.colors.borders.selected
        }
        if (!props.available) {
            return `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`
        }
        return `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.available}%, transparent ${100 - VARIANT_THEME.borders.opacity.available}%)`
    }};
    ${css(
        getTypographyRoleStyle('labelText', {
            fontWeight: VARIANT_THEME.typography.weights.regular,
            letterSpacing: '-0.02em',
        }),
    )}
    font-size: ${VARIANT_THEME.typography.sizes.base};
    color: ${(props) =>
        props.active
            ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
            : VARIANT_THEME.colors.text.primary};
    white-space: nowrap;
    text-align: center;
    word-break: normal;
    flex-shrink: 0;

    &:hover {
        border-color: ${(props) =>
            props.available
                ? VARIANT_THEME.colors.states.active
                : `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`};
    }

    ${(props) => getUnavailableStyles(props.available, props.isClickable)}

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        font-size: ${VARIANT_THEME.typography.sizes.base};
    }

    // Special styling for color variants
    &[data-testid^='color-variant-'] {
        width: ${VARIANT_THEME.layout.sizes.circular.default};
        height: ${VARIANT_THEME.layout.sizes.circular.default};
        min-width: ${VARIANT_THEME.layout.sizes.circular.default};
        min-height: ${VARIANT_THEME.layout.sizes.circular.default};
        padding: 0;
        border-radius: ${VARIANT_THEME.layout.radius.circular};

        @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
            width: ${VARIANT_THEME.layout.sizes.circular.mobile};
            height: ${VARIANT_THEME.layout.sizes.circular.mobile};
            min-width: ${VARIANT_THEME.layout.sizes.circular.mobile};
            min-height: ${VARIANT_THEME.layout.sizes.circular.mobile};
        }
    }
`

export const VariantTitleText = styled.span`
    ${css(
        getTypographyRoleStyle('labelText', {
            fontWeight: 700,
            lineHeight: 1.4,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        }),
    )}
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    font-size: ${VARIANT_THEME.typography.sizes.md};
`

export const VariantTitleValue = styled.span`
    ${css(
        getTypographyRoleStyle('bodyText', {
            fontWeight: 400,
            textTransform: 'none',
        }),
    )}
`

export const UnavailableText = styled.span`
    ${css(
        getTypographyRoleStyle('statusText', {
            lineHeight: 1.4,
            letterSpacing: '-0.1px',
        }),
    )}
    font-size: ${VARIANT_THEME.typography.sizes.md};
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
    margin-top: ${VARIANT_THEME.layout.spacing.xxs};
    display: flex;
    align-items: center;
    gap: ${VARIANT_THEME.layout.spacing.xxs};
    opacity: 0.5;

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        font-size: ${VARIANT_THEME.typography.sizes.base};
    }
`

export const VariantTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${VARIANT_THEME.layout.spacing.xxs};
    margin-bottom: 2px;
`

// Base container with shared styles for both variant images and swatches
const BaseVariantImageContainer = styled.div<{
    isActive: boolean
    isAvailable?: boolean
    isClickable?: boolean
}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${VARIANT_THEME.layout.spacing.xxs};
    position: relative;
    cursor: ${(props) =>
        (props.isClickable ?? props.isAvailable) ? 'pointer' : 'not-allowed'};
    transition: ${VARIANT_THEME.transitions.smooth};
    overflow: hidden;
    box-sizing: border-box;
`

// Container for variant images with price/name (non-swatch)
export const VariantImageContainer = styled(BaseVariantImageContainer)`
    min-width: 70px;
    padding: ${VARIANT_THEME.layout.spacing.xxs};
    border-radius: ${VARIANT_THEME.layout.radius.image};
    justify-content: flex-start;
    width: auto;
    height: auto;
    border: ${(props) => {
        if (!props.isAvailable) {
            return `1px solid color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`
        }
        return props.isActive
            ? `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
            : `1px solid color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.available}%, transparent ${100 - VARIANT_THEME.borders.opacity.available}%)`
    }};
    background-color: ${(props) =>
        props.isActive ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})` : 'transparent'};
    box-shadow: ${(props) =>
        props.isActive ? VARIANT_THEME.shadows.active : 'none'};

    ${(props) =>
        !props.isAvailable
            ? `
        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: ${VARIANT_THEME.overlays.soldOut};
            border-radius: inherit;
            z-index: 1;
        }
    `
            : ''}

    &:hover {
        border-color: ${(props) =>
            props.isAvailable
                ? VARIANT_THEME.colors.states.active
                : `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`};
    }
`

// Container for circular swatches (no price/name)
// The ring is always drawn, transparent when the swatch is neither active nor
// hovered, so colouring it in on hover can't resize the swatch inside it.
export const SwatchVariantImageContainer = styled(BaseVariantImageContainer)`
    min-width: unset;
    padding: 3px;
    border-radius: ${VARIANT_THEME.layout.radius.circular};
    justify-content: center;
    width: ${VARIANT_THEME.layout.sizes.swatch.container};
    height: ${VARIANT_THEME.layout.sizes.swatch.container};
    border: 1px solid
        ${(props) => {
            if (!props.isAvailable) {
                return `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`
            }
            return props.isActive
                ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
                : 'transparent'
        }};
    background-color: transparent;
    box-shadow: none;

    @media (hover: hover) {
        &:hover {
            border-color: ${(props) =>
                props.isAvailable
                    ? `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
                    : `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) ${VARIANT_THEME.borders.opacity.unavailable}%, transparent ${100 - VARIANT_THEME.borders.opacity.unavailable}%)`};
        }
    }

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        width: ${VARIANT_THEME.layout.sizes.swatch.container};
        height: ${VARIANT_THEME.layout.sizes.swatch.container};
    }
`

export const VariantImageWrapper = styled.div<{ isSwatch?: boolean }>`
    position: relative;
    width: 100%;
    height: ${(props) => (props.isSwatch ? '100%' : 'auto')};
    display: flex;
    justify-content: center;
    align-items: center;
`

export const VariantContainer = styled.div`
    margin-bottom: ${VARIANT_THEME.layout.spacing.lg};
`

export const VariantGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${VARIANT_THEME.layout.spacing.sm};
`

export const VariantOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${VARIANT_THEME.layout.spacing.md};
`

export const ImageVariantRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${VARIANT_THEME.layout.spacing.sm};
    overflow-x: auto;
    padding-bottom: ${VARIANT_THEME.layout.spacing.xxs};
    -webkit-overflow-scrolling: touch;
`

export const VariantPriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: ${VARIANT_THEME.layout.spacing.xxs}
        ${VARIANT_THEME.layout.spacing.xxs} 0;
    width: 100%;
    pointer-events: none;
    position: relative;
    z-index: 0;
`

export const VariantPriceInfo = styled.div`
    display: flex;
    text-align: center;
    flex-direction: column;
    gap: 2px;
`

export const VariantPrice = styled.span<{ isActive?: boolean }>`
    ${css(getTypographyRoleStyle('price', { fontWeight: 500 }))}
    font-size: ${VARIANT_THEME.typography.sizes.base};
    color: ${(props) =>
        props.isActive
            ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
            : `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`};
`

export const VariantComparePrice = styled.span`
    ${css(getTypographyRoleStyle('compareAtPrice', { fontWeight: 400 }))}
    color: var(${SUBTLE_PRIMARY_COLOR_VAR});
    font-size: ${VARIANT_THEME.typography.sizes.sm};
    text-decoration: line-through;
`

export const VariantColorName = styled.span<{ isActive?: boolean }>`
    ${css(
        getTypographyRoleStyle('captionText', {
            fontWeight: 500,
            lineHeight: 1.3,
        }),
    )}
    color: ${(props) =>
        props.isActive
            ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
            : `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`};
    font-size: ${VARIANT_THEME.typography.sizes.md};
    margin-bottom: 1px;
    text-align: center;
    width: 100%;

    @media (max-width: ${VARIANT_THEME.breakpoints.mobile}) {
        font-size: ${VARIANT_THEME.typography.sizes.md};
    }
`
